import React, {useEffect, useState} from 'react';
import {Platform, ViewStyle} from 'react-native';
import {useSelector} from 'react-redux';
import {useIsFocused} from '@react-navigation/native';
import {
  holderProfileSelector,
  profileSessionExpiredSelector,
  profileTokenSelector,
} from '../../redux/selectors/profile';
import {canReissueCardSelector} from '../../redux/selectors/cardReissue';
import {
  esiaRegistrationInfoSelector,
  esiaUserHasNoCardsSelector,
  esiaUserNotAcceptSelector,
} from '../../redux/selectors/login';
import {LoginStepSelector} from '../../redux/selectors/runtime/loginStep';
import {LoginState} from '../../shared/models/login/loginStep';
import {useStyles} from '../../shared/hooks/styles';
import {useIsSmallScreenDevice} from '../../shared/hooks/useIsSmallScreenDevice';
import {useIsDesktop} from '../../shared/hooks/useIsDesktop';
import {LoginLayout} from '../login/components/layout';
import {Esia} from '../login/esia';
import {EsiaLogoutWebView} from '../../shared/components/esiaLogoutWebView';
import {useCardReissueLoader} from './hooks/useCardReissueLoader';
import {useCardReissueGuards} from './hooks/useCardReissueGuards';
import {useCardReissueScreenFlow} from './hooks/useCardReissueScreenFlow';
import {useAppDispatch} from '../../redux';
import {setRuntimeLoginStepState} from '../../redux/actions/runtime/loginStep';
import View from './view';

export function CardReissueScreen() {
  const dispatch = useAppDispatch();
  const isFocused = useIsFocused();
  const isDesktop = useIsDesktop();
  const isSmallScreen = useIsSmallScreenDevice();
  const step = useSelector(LoginStepSelector());
  const esiaUserNotAccept = useSelector(esiaUserNotAcceptSelector);
  const userHasNoCards = useSelector(esiaUserHasNoCardsSelector);
  const esiaRegInfo = useSelector(esiaRegistrationInfoSelector);
  const holderProfile = useSelector(holderProfileSelector());
  const canReissueCard = useSelector(canReissueCardSelector());
  const token = useSelector(profileTokenSelector());
  const isSessionExpired = useSelector(profileSessionExpiredSelector());
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);

  const isAuthorized = !!token && !isSessionExpired;
  const isEsia = step === LoginState.esiaLogin;
  const isEsiaMobile = isEsia && Platform.OS !== 'web' && !esiaUserNotAccept;

  useEffect(() => {
    if (
      !isFocused ||
      isEsia ||
      step === LoginState.enter ||
      userHasNoCards ||
      step === LoginState.esiaRegCardUnconfirmed
    )
      return;
    dispatch(setRuntimeLoginStepState(LoginState.enter));
  }, [dispatch, isEsia, isFocused, step, userHasNoCards]);

  const {hideAndLockGlobalLoader, cancelEsia, unlockGlobalLoader, showGlobalLoader} = useCardReissueLoader({
    isFocused,
    isEsia,
    isFormModalOpen,
  });

  const {redirectBack} = useCardReissueGuards({
    isFocused,
    isAuthorized,
    canReissueCard,
    esiaRegInfo,
    cancelEsia,
  });

  const {submitting, esiaLogoutLink, onEsiaClick, onContinue} = useCardReissueScreenFlow({
    isFocused,
    isDesktop,
    isAuthorized,
    esiaRegInfo,
    holderProfile,
    hideAndLockGlobalLoader,
    cancelEsia,
    unlockGlobalLoader,
    showGlobalLoader,
    setIsFormModalOpen,
  });

  const styles = useStyles<Partial<Record<LoginState, ViewStyle>>>(
    () => ({
      [LoginState.esiaLogin]: isEsiaMobile
        ? {
            width: '100%',
            height: isSmallScreen ? '50%' : 'auto',
            ...(isSmallScreen ? {} : {flex: 1}),
            paddingHorizontal: 0,
            paddingTop: 0,
            paddingBottom: 0,
          }
        : undefined,
    }),
    [isEsiaMobile, isSmallScreen],
  );

  if (isEsia) {
    if (Platform.OS === 'web' && !esiaUserNotAccept) {
      return (
        <>
          <Esia />
          <EsiaLogoutWebView link={esiaLogoutLink} />
        </>
      );
    }

    return (
      <>
        <LoginLayout
          title={'Ваша учетная запись\nв ЕСИА не подтверждена'}
          onBack={redirectBack}
          showHelpText={false}
          containerStyle={styles[LoginState.esiaLogin]}
          showLogo={!isEsiaMobile}
          showTitle={!isEsiaMobile}>
          <Esia />
        </LoginLayout>
        <EsiaLogoutWebView link={esiaLogoutLink} />
      </>
    );
  }

  return (
    <>
      <View
        onEsiaClick={(e) => {
          e?.preventDefault?.();
          onEsiaClick();
        }}
        onContinue={onContinue}
        submitting={submitting || isFormModalOpen}
      />
      <EsiaLogoutWebView link={esiaLogoutLink} />
    </>
  );
}
